import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { VideoItem } from 'services/data-manager/types';

import usePrevious from 'utils/hooks/usePrevious';
import { isEmpty } from 'utils/object';
import { isTablet } from 'utils/responsiveness';

import { PlayerError } from 'screens/PlayerModal/types';
import { onBeforeShowPlayerModal } from 'screens/PlayerModal/utils';

const { SCREEN_PLAYER_IN_HOME_DETECTION_MESSAGE } = LANGUAGE_KEYS;

export default function useInHomeDetectionErrorHandler({
    outOfHomeBlockStream,
    playerVisible,
    playerError,
    onBlock,
    onUnblock,
    videoItem,
    isPinDialogVisible = false,
    embedded = false,
    fullscreen = false,
    shouldWarnOnMobileData,
}: {
    outOfHomeBlockStream: boolean;
    playerVisible: boolean;
    playerError: PlayerError;
    onBlock: (message: string) => void;
    onUnblock: () => void;
    videoItem?: VideoItem;
    isPinDialogVisible?: boolean;
    embedded?: boolean;
    fullscreen?: boolean;
    shouldWarnOnMobileData?: boolean;
}) {
    const { t } = useTranslation();
    const needToShowError =
        outOfHomeBlockStream &&
        playerVisible &&
        !isPinDialogVisible &&
        shouldWarnOnMobileData;
    const prevNeedToShowError = useRef(false);
    const prevVideoItem = usePrevious(videoItem, undefined);

    useEffect(() => {
        if (
            needToShowError &&
            !prevNeedToShowError.current &&
            isEmpty(playerError)
        ) {
            if (!isTablet && !embedded && fullscreen) {
                onBeforeShowPlayerModal();
            }

            onBlock(t(SCREEN_PLAYER_IN_HOME_DETECTION_MESSAGE));
            prevNeedToShowError.current = needToShowError;
        }

        if (
            prevNeedToShowError.current &&
            (!needToShowError || prevVideoItem !== videoItem)
        ) {
            prevNeedToShowError.current = false;
            onUnblock();
        }
    }, [
        needToShowError,
        playerError,
        onBlock,
        onUnblock,
        videoItem,
        prevVideoItem,
    ]);
}
